/**
 * legacyOrderService
 *
 * Persists a "legacy" (pre-platform) rental into the system without triggering
 * any notifications (no SendGrid, no Zoho-Sign). The resulting order is marked
 * via the backend flag `isLegacyImport: true` so it can be filtered later in
 * reporting.
 *
 * Sequence:
 *   1. addCustomer            → returns new customer
 *   2. getCustomerEntries     → refresh the Vuex list (mutation in `addCustomer`
 *                               only updates a single entry, see store quirks)
 *   3. addOrder               → returns new order, with offer omitted
 *   4. getOrderEntries        → refresh list
 *   5. createOrderItem        → uses `createOrderItem` (not `addOrderItem`,
 *                               which has a mutation bug overwriting the list)
 *   6. updateStorage          → mark the chosen container as reserved, mirrors
 *                               the behavior of the Movein wizard
 */

const ensureId = (entity, label) => {
  if (!entity || typeof entity !== 'object' || !entity.id) {
    const detail = entity && entity.message ? `: ${entity.message}` : ''
    throw new Error(`${label}-Anlage fehlgeschlagen${detail}`)
  }
  return entity
}

const uploadAttachments = async (store, customerId, attachments) => {
  const errors = []
  const sections = (attachments || []).filter(a => a && a.files && a.files.length > 0)

  for (let i = 0; i < sections.length; i += 1) {
    const section = sections[i]
    try {
      const formData = new FormData()
      section.files.forEach(f => formData.append('files', f))
      const uploadConfig = { headers: { 'Content-Type': 'multipart/form-data' } }
      // eslint-disable-next-line no-await-in-loop
      const uploaded = await store.dispatch('addFile', formData, uploadConfig)
      if (!Array.isArray(uploaded) || uploaded.length === 0) {
        throw new Error('Upload lieferte keine Datei-IDs zurück')
      }
      // eslint-disable-next-line no-await-in-loop
      await store.dispatch('addAttachment', {
        customer: customerId,
        attachment_type: section.typeId,
        files: uploaded.map(f => f.id),
        note: section.note || '',
      })
    } catch (err) {
      errors.push({
        typeId: section.typeId,
        fileNames: section.files.map(f => f.name),
        error: err && err.message ? err.message : String(err),
      })
    }
  }

  return errors
}

const resolveCustomer = async (store, customerInput) => {
  if (customerInput && customerInput.id) {
    const lookup = store.getters && typeof store.getters.customerById === 'function'
      ? store.getters.customerById(customerInput.id)
      : (store.getters && store.getters.customerById)
    const existing = typeof lookup === 'function' ? lookup(customerInput.id) : lookup
    if (!existing || !existing.id) {
      throw new Error(`Kunden-Lookup fehlgeschlagen (id=${customerInput.id})`)
    }
    return existing
  }
  const created = ensureId(
    await store.dispatch('addCustomer', { ...customerInput }),
    'Kunden',
  )
  await store.dispatch('getCustomerEntries')
  return created
}

export const createLegacyOrder = async (store, payload) => {
  const {
    customer: customerInput, storage, rent, attachments,
  } = payload

  const customer = await resolveCustomer(store, customerInput)

  // Create the OrderItem first (without order FK). Strapi 3 needs the
  // `order.order_items` array on the order itself in order to populate the
  // orders__order_items junction table — setting the FK on the item alone
  // skips the junction and breaks the reverse-relation lookup (order list
  // would show "0 Positionen" / "Beendet" until a junction row exists).
  const orderItem = ensureId(
    await store.dispatch('createOrderItem', {
      order: null,
      storage: storage.id,
      price: rent.price,
      rentStart: rent.rentStart,
      rentEnd: rent.rentEnd || null,
      moveOutDate: null,
      zohoSignStatus: 'not_sent',
    }),
    'Mietposition',
  )

  const order = ensureId(
    await store.dispatch('addOrder', {
      customer: customer.id,
      isLegacyImport: true,
      orderDate: rent.rentStart,
      order_items: [orderItem.id],
    }),
    'Auftrags',
  )

  await store.dispatch('updateStorage', {
    id: storage.id,
    storageReservation: true,
  })

  const attachmentErrors = await uploadAttachments(store, customer.id, attachments)

  // Refresh orders list LAST so the store contains the order with its populated
  // order_items relation. Doing this earlier would snapshot the order without
  // its items (race against createOrderItem) and cause OrdersList to show
  // "Beendet" / "0 Positionen" until the next reload.
  await store.dispatch('getOrderEntries')

  return {
    customer, order, orderItem, attachmentErrors,
  }
}

export default { createLegacyOrder }
